import { useMemo } from 'react';
import { DsButton } from '@/modules/internal-v2/ds';
import type {
  ResourceType,
  ReviewIdentityNode,
} from '@/api/specifications/specifications.types';
import { ProposalRow } from './proposal-row.component';
import type { MergeTarget, Selection } from './selection.types';

interface IdentitySectionProps {
  nodes: ReviewIdentityNode[];
  isLoading: boolean;
  canManage: boolean;
  selection: Selection;
  onSelectChange: (key: string, next: boolean) => void;
  onSelectMany: (entries: { key: string; selected: boolean }[]) => void;
  onApprove: (resource: ResourceType, id: number) => void;
  onReject: (resource: ResourceType, id: number) => void;
  onApproveBranch: (productLineId: number) => void;
  onOpenMerge: (target: MergeTarget) => void;
}

/**
 * Identity tab content. Lines are grouped by manufacturer; each line carries
 * its still-pending models nested below it.
 */
export function IdentitySection({
  nodes,
  isLoading,
  canManage,
  selection,
  onSelectChange,
  onSelectMany,
  onApprove,
  onReject,
  onApproveBranch,
  onOpenMerge,
}: IdentitySectionProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, { id: number; name: string; nodes: ReviewIdentityNode[] }>();
    for (const n of nodes) {
      const key = `${n.productLine.manufacturerId}`;
      const bucket = map.get(key);
      if (bucket) bucket.nodes.push(n);
      else map.set(key, {
        id: n.productLine.manufacturerId,
        name: n.productLine.manufacturerName,
        nodes: [n],
      });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [nodes]);

  if (isLoading && nodes.length === 0) {
    return <div className="sp-empty">Lade Vorschläge…</div>;
  }
  if (!isLoading && nodes.length === 0) {
    return (
      <div className="sp-empty">
        <div className="big">Keine offenen Vorschläge in Identität</div>
        <div>
          Alles freigegeben — oder Filter zu eng. Starte eine neue Extraktion über
          den Button oben rechts.
        </div>
      </div>
    );
  }

  return (
    <>
      {grouped.map((group) => {
        const pendingKeys = collectPendingKeys(group.nodes);
        const allSelected =
          pendingKeys.length > 0 &&
          pendingKeys.every((k) => selection.has(k));

        return (
          <section key={group.id} className="sp-section">
            <header className="sp-section-head">
              <div className="sp-section-title">{group.name}</div>
              <div className="sp-section-meta">
                {group.nodes.length} {group.nodes.length === 1 ? 'Linie' : 'Linien'}
                {countPendingModels(group.nodes) > 0 &&
                  ` · ${countPendingModels(group.nodes)} offene Modelle`}
              </div>
              {canManage && pendingKeys.length > 0 && (
                <div className="sp-section-actions">
                  <DsButton
                    variant="ghost"
                    onClick={() =>
                      onSelectMany(
                        pendingKeys.map((k) => ({ key: k, selected: !allSelected })),
                      )
                    }
                  >
                    {allSelected ? 'Auswahl aufheben' : `Alle ${pendingKeys.length} auswählen`}
                  </DsButton>
                </div>
              )}
            </header>

            {group.nodes.map((node) => {
              const line = node.productLine;
              const lineKey = `productLine:${line.id}`;
              return (
                <div key={line.id}>
                  <ProposalRow
                    selectionKey={lineKey}
                    crumb={<>{line.manufacturerName} · {line.category}</>}
                    name={line.name}
                    state={line.state}
                    itemCount={line.itemCount}
                    sampleItems={line.sampleItems}
                    canApprove={line.state === 'proposed'}
                    canManage={canManage}
                    selected={selection.has(lineKey)}
                    onSelectChange={(next) => onSelectChange(lineKey, next)}
                    onApprove={() => onApprove('productLine', line.id)}
                    onMerge={() =>
                      onOpenMerge({
                        resource: 'productLine',
                        sourceId: line.id,
                        sourceLabel: line.name,
                        scope: {
                          manufacturerId: line.manufacturerId,
                          category: line.category,
                        },
                      })
                    }
                    onReject={() => onReject('productLine', line.id)}
                    extra={
                      canManage && line.state === 'proposed' && node.modelsNeedingReview.length > 0 && (
                        <DsButton
                          variant="ghost"
                          onClick={() => onApproveBranch(line.id)}
                          title="Linie + alle vorgeschlagenen Modelle in einer Transaktion freigeben"
                        >
                          Branch freigeben
                        </DsButton>
                      )
                    }
                  />
                  {node.modelsNeedingReview.map((m) => {
                    const modelKey = `model:${m.id}`;
                    const modelCanApprove =
                      line.state === 'approved' && m.state === 'proposed';
                    return (
                      <ProposalRow
                        key={m.id}
                        selectionKey={modelKey}
                        crumb="Modell"
                        name={m.name}
                        state={m.state}
                        itemCount={m.itemCount}
                        sampleItems={m.sampleItems}
                        canApprove={modelCanApprove}
                        canManage={canManage}
                        selectable={modelCanApprove}
                        selected={selection.has(modelKey)}
                        onSelectChange={(next) => onSelectChange(modelKey, next)}
                        onApprove={() => onApprove('model', m.id)}
                        onMerge={() =>
                          onOpenMerge({
                            resource: 'model',
                            sourceId: m.id,
                            sourceLabel: m.name,
                            scope: { productLineId: line.id },
                          })
                        }
                        onReject={() => onReject('model', m.id)}
                        extra={
                          m.conflictsWith && (
                            <span className="sp-conflict" title="Bereits freigegebenes Schwester-Modell vorhanden">
                              Konflikt mit #{m.conflictsWith.siblingApprovedModelId}
                            </span>
                          )
                        }
                        child
                      />
                    );
                  })}
                </div>
              );
            })}
          </section>
        );
      })}
    </>
  );
}

function collectPendingKeys(nodes: ReviewIdentityNode[]): string[] {
  const keys: string[] = [];
  for (const n of nodes) {
    if (n.productLine.state === 'proposed') keys.push(`productLine:${n.productLine.id}`);
    for (const m of n.modelsNeedingReview) {
      if (n.productLine.state === 'approved' && m.state === 'proposed') {
        keys.push(`model:${m.id}`);
      }
    }
  }
  return keys;
}

function countPendingModels(nodes: ReviewIdentityNode[]): number {
  let n = 0;
  for (const node of nodes) n += node.modelsNeedingReview.length;
  return n;
}
